import { Component, useState } from 'react'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'

function ErrorContent({ error }) {
    const [copied, setCopied] = useState(false)
    const [showDetails, setShowDetails] = useState(false)

    const errorMessage = String(error?.message || 'Unknown error')
    const errorStack = String(error?.stack || '')

    const handleCopy = () => {
        const text = [
            'Error: ' + errorMessage,
            '',
            'Stack Trace:',
            errorStack,
            '',
            'URL: ' + window.location.href,
            'Time: ' + new Date().toISOString(),
            'UserAgent: ' + navigator.userAgent,
        ].join('\n')

        navigator.clipboard.writeText(text).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        })
    }

    return (
        <Container maxWidth='sm' disableGutters>
            <Stack
                gap={2}
                justifyContent='center'
                alignItems='center'
                sx={{
                    p: 3,
                    maxWidth: '400px',
                    width: '100%',
                    minHeight: '100dvh',
                    margin: 'auto',
                }}
            >
                <Typography
                    component='h1'
                    sx={{
                        color: '#FF2633',
                        fontSize: '48px',
                        fontWeight: '700',
                        textAlign: 'center',
                    }}
                >
                    Oops!
                </Typography>

                <Typography
                    component='p'
                    sx={{
                        color: '#141414',
                        fontSize: '16px',
                        fontWeight: '600',
                        textAlign: 'center',
                    }}
                >
                    Something went wrong
                </Typography>

                <Typography
                    component='p'
                    sx={{
                        color: '#666',
                        fontSize: '14px',
                        fontWeight: '400',
                        textAlign: 'center',
                    }}
                >
                    {errorMessage}
                </Typography>

                {errorStack && (
                    <>
                        <Typography
                            component='p'
                            onClick={() => setShowDetails(!showDetails)}
                            sx={{
                                color: '#501BC0',
                                fontSize: '13px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                            }}
                        >
                            {showDetails ? 'Hide details' : 'Show details'}
                        </Typography>

                        {showDetails && (
                            <Box
                                sx={{
                                    width: '100%',
                                    maxHeight: '200px',
                                    overflow: 'auto',
                                    bgcolor: '#f5f5f5',
                                    borderRadius: 1,
                                    p: 1.5,
                                }}
                            >
                                <Typography
                                    component='pre'
                                    sx={{
                                        color: '#666',
                                        fontSize: '11px',
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        m: 0,
                                    }}
                                >
                                    {errorStack}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}

                <Stack direction='row' gap={1} sx={{ mt: 1 }}>
                    <Button
                        variant='contained'
                        onClick={handleCopy}
                        sx={{ minWidth: '120px' }}
                    >
                        {copied ? 'Copied!' : 'Copy Error'}
                    </Button>
                    <Button
                        variant='outlined'
                        onClick={() => window.location.reload()}
                    >
                        Reload
                    </Button>
                </Stack>
            </Stack>
        </Container>
    )
}

class ErrorBoundary extends Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error }
    }

    render() {
        if (this.state.hasError) {
            return (
                <ErrorContent error={this.state.error} />
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
